<template>
    <TransitionRoot :show="store.openSearchDialog" as="template">
        <Dialog as="div" :initialFocus="queryTextRef" class="relative z-50" @close="closeConfirmation">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
                             leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"/>
            </TransitionChild>

            <div class="fixed inset-0 z-10 overflow-y-auto">
                <div class="flex items-start justify-center min-h-full px-8 mt-8">
                    <DialogPanel
                            class="relative w-full max-w-5xl px-4 py-8 overflow-hidden text-left transition-all transform border border-2 rounded-lg shadow-sm shadow-xl bg-clay-50 lg:w-2/3 sm:mt-24 lg:px-8 border-clay-500">
                        <div class="absolute top-0 right-0 block pt-4 pr-4">
                            <button class="text-gray-400 bg-white rounded-md hover:text-clay-800 focus:outline-hidden"
                                    type="button"
                                    @click="closeConfirmation">
                                <span class="sr-only">Close</span>
                                <svg class="w-6 h-6" fill="none" stroke="currentColor"
                                     stroke-width="1.5" viewBox="0 0 24 24"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>

                            </button>
                        </div>
                        <div>
                            <div
                                    class="max-w-5xl mx-auto mt-8 overflow-hidden transition-all transform">
                                <div class="relative underline">
                                    <svg aria-hidden="true"
                                         class="pointer-events-none absolute top-3.5 left-4 h-5 w-5 text-gray-400"
                                         viewbox="0 0 20 20">
                                        <path clip-rule="evenodd"
                                              d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
                                              fill-rule="evenodd"></path>
                                    </svg>
                                    <input v-model="queryText" 
                                           @input="onQueryTextChanged"
                                           ref="queryTextRef"
                                           aria-controls="options" 
                                           aria-expanded="false"
                                           class="w-full h-12 pr-4 text-gray-800 placeholder-gray-400 bg-transparent bg-white border rounded-md focus:font-semibold pl-11 sm:text-2xl focus:border-2 focus:border-clay-500 border-clay-300"
                                           placeholder="Search StageAgent" role="combobox" type="text">
                                </div>
                                <p class="block px-10 pt-2 italic text-gray-600">Input a name, title or keyword</p>
                                
                                <SkeletonCard class="mt-3" v-if="isRetrieving" card-style="long" :num-of-cards=1 />
                                <div v-else>
                                    <div v-if="hasResults" class="p-4 mt-4 bg-white border divide-y divide-gray-200 rounded-lg border-clay-300">
                                        <div class="">
                                            <div class="flex flex-wrap items-center justify-between sm:flex-nowrap">
                                                <div class="">
                                                    <h3 class="text-base font-semibold leading-6 text-gray-900">Suggested</h3>
                                                </div>
                                                <div class="shrink-0">
                                                    <a :href="`/search?term=${queryText}`"
                                                       class="relative inline-flex items-center px-3 py-2 text-sm font-semibold text-gray-800 underline rounded-md hover:text-gray-900">See
                                                        all results</a>
                                                </div>
                                            </div>
                                        </div>
                                        <ul v-if="shows.length > 0" class="grid-cols-1 py-2 text-sm text-gray-800 sm:grid sm:grid-cols-3"
                                            id="options"
                                            role="listbox">
                                            <li class="col-span-3 px-4 pt-2 text-lg cursor-default select-none" id="option-1"
                                                role="option" tabindex="-1">
                                                Shows
                                            </li>
                                            <li v-for="show in shows" :key="show.id" class="px-4 py-2">
                                                <ShowCardMinimal :show="show" />
                                            </li>
                                        </ul>
                                        <ul v-if="learningModules.length > 0" class="grid-cols-1 py-2 text-sm text-gray-800 sm:grid sm:grid-cols-3 "
                                            id="options"
                                            role="listbox">
                                            <li class="col-span-3 px-4 pt-2 text-lg cursor-default select-none" id="option-1"
                                                role="option" tabindex="-1">
                                                Learning Modules
                                            </li>
                                            <li v-for="guide in learningModules" :key="guide.id" class="px-4 py-2">
                                                <LearningModuleCardMinimal :guide="guide" />
                                            </li>
                                        </ul>

                                        <div class="py-2 text-sm text-gray-800 ">
                                            <p class="block w-full px-4 pt-2 mb-2 text-lg cursor-default select-none flex-0">
                                                Other Results</p>
                                            <div v-for="result in otherResults.filter(x=>x.totalResults > 0)"
                                                 :key="result.category"
                                                 class="inline-block mt-2 space-x-3 space-y-2 sm:ml-4 sm:mt-0">
                                                <div class="flex flex-wrap items-center -m-1">
                                                    <a :href="`/search?term=${encodeURIComponent(queryText)}&category=${encodeURIComponent(result.category)}`"
                                                       class="inline-flex px-2 py-1 m-1 mb-2 text-sm font-semibold capitalize rounded-full bg-powder-200 text-powder-700 hover:bg-powder-100 hover:text-powder-700 hover:shadow-sm">
                                                        <span>{{result.category}} ({{result.totalResults}})</span>
                                                    </a>
                                                </div>
                                            </div>
                                        </div>

                                        <div v-if="relatedTags.length > 0" class="py-2 text-sm text-gray-800 ">
                                            <p class="block w-full px-4 pt-2 mb-2 text-lg cursor-default select-none flex-0">
                                                Related Searches</p>
                                            <div v-for="tag in relatedTags" :key="tag" class="inline-block mt-2 space-x-3 space-y-2 sm:ml-4 sm:mt-0">
                                                <div class="flex flex-wrap items-center -m-1">
                                                    <a @click="onRelatedTagClicked(tag)" 
                                                       href="#"
                                                       class="inline-flex px-2 py-1 m-1 mb-2 text-xs font-medium border rounded-full text-clay-800 bg-clay-50 hover:border-clay-900 hover:border hover:text-clay-700 hover:bg-clay-100">
                                                        <span>{{tag}}</span>
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                        <a :href="`/search?term=${queryText}`"
                                           class="rounded-full bg-clay-950 px-2.5 py-1.5  font-semibold text-clay-100 hover:bg-clay-900 hover:text-clay-50 hover:shadow-sm mt-4 min-w-full block text-center"  href="">
                                            See all results
                                        </a>
                                    </div>
                                    <div v-else>
                                        <div class="px-4 py-2 ">
                                            <div class="flex flex-wrap items-center justify-between sm:flex-nowrap">
                                                <h3 class="text-sm leading-6 text-clay-800">
                                                    {{ isRetrieving === false && queryText?.length >= 3 ? 'No results found': '' }}
                                                    <div class="mt-2" v-if="hasSuggestions">
                                                        <span class="mr-3 text-clay-900">Did you mean:</span>
                                                        <span class="mr-2 text-clay-800 underline cursor-pointer hover:no-underline" 
                                                              v-for="suggestion in suggestionsWhenNoResults" 
                                                           @click.prevent="onSuggestionClicked(suggestion)">{{suggestion}}
                                                        </span>
                                                    </div>
                                                </h3>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>


</template>

<script lang="ts" setup>
// @ts-ignore
import {computed, ref} from "vue";
import {Dialog, DialogPanel, TransitionChild, TransitionRoot} from '@headlessui/vue';
import {client} from '@/shared';
import {
    CategoryWithCount, 
    LearningModuleDtoLightWeight,
    SearchGuidesUsingAutocomplete,
    SearchGuidesUsingAutocompleteResponse, 
    ShowDto,
} from "@/dtos";
import ShowCardMinimal from "../Guides/Shows/ShowCardMinimal.vue";
import LearningModuleCardMinimal from "../LearningModules/LearningModuleCardMinimal.vue";
// @ts-ignore
import { useDebounceFn } from '@vueuse/core';
import SkeletonCard from "@/main/components/skeletons/SkeletonCard.vue";
import {store} from "@/store";
import {trackEvent} from '@/shared';

const queryTextRef = ref(null);

const closeConfirmation = async () => {
    store.openSearchDialog = false;
}

const shows = ref<Array<ShowDto>>([]);
const learningModules = ref<Array<LearningModuleDtoLightWeight>>([]);
const otherResults = ref<Array<CategoryWithCount>>([]);
const relatedTags = ref<string>([]);
const queryText = ref('');
const hasResults = ref(false);
const isRetrieving = ref(false);

const onQueryTextChanged = useDebounceFn(async () => {

    if (queryText.value.length >= 3) {
        isRetrieving.value = true;
        await client.get<SearchGuidesUsingAutocompleteResponse>(new SearchGuidesUsingAutocomplete({q: queryText.value}))
            .then(res => {
                shows.value = res.shows;
                learningModules.value = res.learningModules;
                otherResults.value = res.categoriesWithCounts;
                relatedTags.value = res.relatedTags;
                hasResults.value = otherResults.value.some((r: any) => r.totalResults > 0);
                suggestionsWhenNoResults.value = res.suggestions;
                isRetrieving.value = false;
            });
        
        trackEvent( 'search', {
            search_term: queryText.value
        }, false);
    }
}, 1000)

const onRelatedTagClicked = async (clickedTag: string) => {
    queryText.value = clickedTag;
    await onQueryTextChanged();
}

const suggestionsWhenNoResults = ref<string>([]);
const hasSuggestions = computed(() => {
    return isRetrieving.value === false && queryText?.value.length >= 3 && suggestionsWhenNoResults.value.length > 0;
});

const onSuggestionClicked = async (suggestion: string) => {
    queryText.value = suggestion;
    await onQueryTextChanged();
}

</script>
<style>

</style>